const STOREFRONT_TOKEN = 'eb11a16cfbfdd9fe0e1c6c0109cbbd6c';
const API_VERSION = '2025-10';

const GRAPHQL_ENDPOINT = `/api/${API_VERSION}/graphql.json`;

/**
 * Fetch products of a given type directly from Shopify's Storefront API.
 * Shopify's search index does the type-filtering — this is not a
 * "load everything then filter" call.
 */
async function fetchProductsByType(productType, excludeId, maxProducts = 6) {
  if (!productType) return [];

  // Fetch a couple extra so we still hit `maxProducts` after excluding
  // the current product from the results.
  const first = maxProducts + 2;

  const query = `
    query RelatedByType($searchQuery: String!, $first: Int!) {
      products(first: $first, query: $searchQuery) {
        edges {
          node {
            id
            title
            handle
            vendor
            availableForSale
            featuredImage {
              url
              altText
              width
              height
            }
            priceRange {
              minVariantPrice {
                amount
                currencyCode
              }
            }
            compareAtPriceRange {
              minVariantPrice {
                amount
                currencyCode
              }
            }
          }
        }
      }
    }
  `;

  // Storefront search syntax: filter by exact product_type
  const searchQuery = `product_type:'${productType.replace(/'/g, "\\'")}'`;

  const response = await fetch(GRAPHQL_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN,
    },
    body: JSON.stringify({
      query,
      variables: { searchQuery, first },
    }),
  });

  if (!response.ok) {
    console.error('Storefront API request failed', response.status);
    return [];
  }

  const { data, errors } = await response.json();

  if (errors) {
    console.error('Storefront API GraphQL errors', errors);
    return [];
  }

  const products = data.products.edges.map((edge) => edge.node);

  // Exclude current product, then cap to maxProducts
  const excludeGid = `gid://shopify/Product/${excludeId}`;
  return products.filter((p) => p.id !== excludeGid).slice(0, maxProducts);
}

/**
 * Format a money amount using the shop's currency.
 */
function formatMoney(amount, currencyCode) {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2,
  }).format(amount);
}

/**
 * Build a card node from the <template id="related-product-card-template">
 * declared in related-products.liquid. Mirrors the Diwali page's
 * buildCard(template, product) approach: clone the template once per
 * product and fill in [data-slot] elements, rather than assembling
 * markup as HTML strings.
 */
function buildCard(template, product, showVendor) {
  const price = product.priceRange.minVariantPrice;
  const compareAt = product.compareAtPriceRange?.minVariantPrice;
  const isSoldOut = !product.availableForSale;
  const isOnSale = !!compareAt && parseFloat(compareAt.amount) > parseFloat(price.amount);

  let discountPercent = 0;
  if (isOnSale) {
    discountPercent = Math.round(
      ((parseFloat(compareAt.amount) - parseFloat(price.amount)) / parseFloat(compareAt.amount)) * 100
    );
  }

  const primaryImg = product.featuredImage;
  const productUrl = `/products/${product.handle}`;

  const node = template.content.firstElementChild.cloneNode(true);

  // Image (hover zoom only — no secondary-image swap, since some
  // products only have one image)
  const primaryImgEl = node.querySelector('[data-slot="primary-image"]');
  if (primaryImg?.url) {
    primaryImgEl.src = primaryImg.url;
    primaryImgEl.srcset = `${primaryImg.url} 1x`;
    primaryImgEl.width = primaryImg.width || 633;
    primaryImgEl.height = primaryImg.height || 633;
    primaryImgEl.alt = primaryImg.altText || product.title;
  }

  // Titles / links
  ['title-link-1', 'title-link-2'].forEach((slot) => {
    const el = node.querySelector(`[data-slot="${slot}"]`);
    el.href = productUrl;
    el.textContent = product.title;
  });
  node.querySelector('[data-slot="details-link"]').href = productUrl;

  // Vendor
  const vendorEl = node.querySelector('[data-slot="vendor"]');
  if (showVendor && product.vendor) {
    vendorEl.textContent = product.vendor;
    vendorEl.hidden = false;
  }

  // Top badge (sold out / discount)
  const badgeTop = node.querySelector('[data-slot="badge-top"]');
  const badgeTopText = node.querySelector('[data-slot="badge-top-text"]');
  if (isSoldOut) {
    badgeTop.hidden = false;
    badgeTopText.textContent = 'Sold out';
    badgeTopText.classList.add('color-scheme-3');
  } else if (discountPercent > 0) {
    badgeTop.hidden = false;
    badgeTopText.textContent = `${discountPercent}% OFF`;
    badgeTopText.classList.add('color-scheme-4');
  }

  // Sale badge (bottom, only when on sale and not sold out)
  if (!isSoldOut && discountPercent > 0) {
    node.querySelector('[data-slot="badge-sale"]').hidden = false;
  }

  // Price
  const priceWrap = node.querySelector('[data-slot="price-wrap"]');
  if (isOnSale) priceWrap.classList.add('price--on-sale');

  node.querySelector('[data-slot="price-regular"]').textContent = formatMoney(price.amount, price.currencyCode);
  node.querySelector('[data-slot="price-sale"]').textContent = formatMoney(price.amount, price.currencyCode);
  node.querySelector('[data-slot="price-compare"]').textContent = isOnSale
    ? formatMoney(compareAt.amount, compareAt.currencyCode)
    : '';

  return node;
}

class RelatedProductsRandom extends HTMLElement {
  async connectedCallback() {
    const productType = this.dataset.productType;
    const productId = this.dataset.productId;
    const max = parseInt(this.dataset.maxProducts, 10) || 6;
    const heading = this.dataset.heading || 'You may also like';
    const showVendor = this.dataset.showVendor === 'true';

    if (!productType) return;

    const products = await fetchProductsByType(productType, productId, max);

    if (products.length === 0) {
      this.remove(); // nothing to show, don't render an empty section
      return;
    }

    const template = document.querySelector('#related-product-card-template');
    if (!template) {
      console.error('related-product-card-template not found');
      return;
    }

    this.innerHTML = `
      <h2 class="related-products__heading inline-richtext h1">${heading}</h2>
      <ul class="grid product-grid grid--3-col-desktop grid--2-col-tablet-down" role="list"></ul>
    `;

    const grid = this.querySelector('ul');
    products.forEach((product) => {
      grid.appendChild(buildCard(template, product, showVendor));
    });
  }
}

if (!customElements.get('related-products-random')) {
  customElements.define('related-products-random', RelatedProductsRandom);
}